/**
 * @render react
 * @name Footer
 * @example
 * <Footer />
 * Example when mobile sticky ad should not be rendered on the page:
 * <Footer mobileStickyAd={false} />
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDidomiPreferencesMenuItem } from 'polaris-coreweb/exports';
import { Ad } from '@autovia-uk/polaris-components/components/atoms/Ad';
import { Navigation } from '@autovia-uk/polaris-components/components/molecules/Navigation';
import { withPolaris } from '@autovia-uk/polaris-components/components/protons/Polaris';
import { Social } from '@autovia-uk/polaris-components/components/molecules/Social';
import { Newsletter } from '@autovia-uk/polaris-components/components/organisms/Newsletter';
import { getSkipLinkComponent } from '@autovia-uk/polaris-components/sharedPartials/getSkipLinkComponent';
import './_style.scss';

/**
 * HELPERS
 * In CMS new lines added via special symbols \r\n
 * swap them for line break tag
 * simple swap '\r\n' -> '<br />' in string doesn't give wanted solution, so making react components is a must
 * https://medium.com/@kevinsimper/react-newline-to-break-nl2br-a1c240ba746
 */
function renderCMSText(str) {
  if (typeof str !== 'string') return '';

  const newLine = '\r\n';
  if (!str.includes(newLine)) return str;

  return (
    <>
      {
        str.split(newLine).map((line, lineIndex) => (
          <React.Fragment key={`line_${lineIndex}`}>
            {line}
            <br />
          </React.Fragment>
        ))
      }
    </>
  );
}

const Footer = ({
  children,
  context: {
    cssPrefix,
    didomi,
    footer: {
      navigation,
      newsletterConfig,
    },
    globalSettings: {
      copyright,
    },
    layoutType,
    social,
    type,
  },
  mobileStickyAd,
}) => {
  const isCommercialPage = layoutType === 'commercialPage';
  const isCommercialPageTitleImage = layoutType === 'commercialPageTitleImage';

  const didomiPreferencesItem = useDidomiPreferencesMenuItem({
    enabled: Boolean(didomi?.publicApiKey && didomi?.noticeId),
  });

  const footerNavigation = navigation ? [...navigation] : [];
  if (didomiPreferencesItem) {
    footerNavigation.push(didomiPreferencesItem);
  }

  // Get the index of the last column footer nav so it only has 4 items in 3rd column.
  // Can be adjusted later in the future.
  const lastFooterNavColumnIndex = (footerNavigation.length < 4) ? footerNavigation.length : footerNavigation.length - 4;

  return (
    <>
      {children && (
        <div className={`${cssPrefix}__footer--children`}>
          {children}
        </div>
      )}

      {type && type !== 'loading' && getSkipLinkComponent()}

      <footer
        className={`${cssPrefix}__footer`}
        id="footer"
      >
        <div className={`${cssPrefix}__footer--inner`}>
          <div className={`${cssPrefix}__footer--row`}>
            <div className={`${cssPrefix}__content ${cssPrefix}__content-padding -footer-nav`}>

              <div className={`${cssPrefix}__footer--navigation`}>
                {navigation && (
                  <Navigation
                    content={footerNavigation.slice(0, lastFooterNavColumnIndex)}
                  />
                )}

                {navigation && (
                  <Navigation
                    content={footerNavigation.slice(lastFooterNavColumnIndex)}
                    extraClassNames={{
                      '-secondary-nav': true,
                    }}
                  />
                )}
              </div>

              <div className={`${cssPrefix}__footer--newsletter`}>
                <Newsletter
                  mainTitle={(newsletterConfig && newsletterConfig.mainTitle) || ''}
                  title={(newsletterConfig && newsletterConfig.title) || ''}
                  subtitle={(newsletterConfig && newsletterConfig.subtitle) || ''}
                  cta_label={(newsletterConfig && newsletterConfig.ctaLabel) || ''}
                  placeholder={(newsletterConfig && newsletterConfig.placeholder) || ''}
                  extraClassNames={{ '-footer': true }}
                  position="footer"
                  openModal={false}
                  toggleModal={null}
                />
              </div>
            </div>
          </div>

          <div className={`${cssPrefix}__footer--row`}>
            <div className={`${cssPrefix}__content ${cssPrefix}__content-padding`}>
              <div className={`${cssPrefix}__footer--copyright`}>
                <p>{renderCMSText(copyright)}</p>
              </div>

              <Social
                platforms={social.follow.platforms}
                extraClassNames={{ [`${cssPrefix}__social -footer`]: true }}
              />
            </div>
          </div>
        </div>

        {mobileStickyAd && !(isCommercialPage || isCommercialPageTitleImage) && (
          <Ad
            id="refresh-stickyfooter"
            renderWhenViewable={false}
            type="stickyfooter"
            targeting={{
              position: 'stickyfooter',
              placement: 'stickyfooter',
              refresh: 'yes',
            }}
            isMobileSticky
          />
        )}
      </footer>
    </>
  );
};

/**
 * Valid prop types
 */
Footer.propTypes = {
  children: PropTypes.node,
  context: PropTypes.shape({
    footer: PropTypes.shape({
      logo: PropTypes.shape({
        src: PropTypes.string.isRequired,
        height: PropTypes.number,
        width: PropTypes.number,
        url: PropTypes.string,
        alt: PropTypes.string,
      }).isRequired,
      secondaryLogo: PropTypes.shape({
        src: PropTypes.string.isRequired,
        height: PropTypes.number,
        width: PropTypes.number,
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
      navigation: PropTypes.arrayOf(PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
      })),
      newsletterConfig: PropTypes.shape({
        mainTitle: PropTypes.string,
        title: PropTypes.string,
        subtitle: PropTypes.string,
        ctaLabel: PropTypes.string,
        placeholder: PropTypes.string,
      }),
    }),
    layoutType: PropTypes.string,
    social: PropTypes.shape({
      follow: PropTypes.shape(),
    }),
    didomi: PropTypes.shape({
      noticeId: PropTypes.string,
      publicApiKey: PropTypes.string,
      sdkPath: PropTypes.string,
    }),
    globalSettings: PropTypes.shape({
      copyright: PropTypes.string,
    }),
    cssPrefix: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  mobileStickyAd: PropTypes.bool,
};

/**
 * Default props
 */
Footer.defaultProps = {
  children: null,
  mobileStickyAd: true,
};

export default withPolaris(Footer);
